import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { clinicInfo } from "../mock/data";
import { MapPin, Phone, Clock, MessageCircle, Instagram, Send, CalendarDays, Sun, Sunset } from "lucide-react";
import { toast } from "sonner";

const loadCalendar = () => import("./ui/calendar");

const Calendar = lazy(() =>
  loadCalendar().then((module) => ({ default: module.Calendar }))
);
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { trackLead } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fetchWithTimeout = async (url, options = {}, timeout = 15000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }
};

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const pad2 = (value) => String(value).padStart(2, "0");

const formatDisplayDate = (date) =>
  `${pad2(date.getDate())} ${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;

const formatISODate = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const startOfToday = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};


export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: "General Consultation",
    preferred_date: "",
    preferred_time: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const backendWakeStarted = useRef(false);

  const wakeBackend = () => {
    if (backendWakeStarted.current) return;
    backendWakeStarted.current = true;

    // Wake a sleeping backend while the patient fills the form.
    // Never wait for this request before submitting.
    fetchWithTimeout(`${API}/health`, {}, 15000).catch(() => {});
  };
    useEffect(() => {
      const wakeOnInteraction = () => wakeBackend();

      window.addEventListener("pointerdown", wakeOnInteraction, {
        once: true,
        passive: true,
      });
      window.addEventListener("keydown", wakeOnInteraction, { once: true });

      const section = document.getElementById("contact");
      let observer = null;

      if (section && "IntersectionObserver" in window) {
        observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              wakeBackend();
              observer.disconnect();
            }
          },
          {
            rootMargin: "500px 0px",
            threshold: 0,
          }
        );

        observer.observe(section);
      }

      return () => {
        window.removeEventListener("pointerdown", wakeOnInteraction);
        window.removeEventListener("keydown", wakeOnInteraction);

        if (observer) {
          observer.disconnect();
        }
      };
    }, []);


  const submit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.phone) {
      toast.error("Please share your name and WhatsApp number");
      return;
    }

    if (!form.preferred_date) {
      toast.error("Please select your preferred date");
      return;
    }

    if (!form.preferred_time) {
      toast.error("Please select your preferred time");
      return;
    }

    const selectedDate = new Date(`${form.preferred_date}T12:00:00`);

    if (selectedDate.getDay() === 6) {
      toast.error("The clinic is closed on Saturdays. Please choose another date.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetchWithTimeout(
        `${API}/appointments`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: form.name,
            phone: form.phone,
            email: form.email || null,
            service: form.service,
            preferred_date: form.preferred_date,
            preferred_time: form.preferred_time,
            message: form.message,
          }),
        },
        75000
      );

      const data = await response.json();

      if (!response.ok) {
        const error = new Error("Appointment request failed");
        error.response = { data };
        throw error;
      }

      toast.success(
        `Thanks ${data.name.split(" ")[0]}! Your appointment request has been sent.`
      );

      trackLead("appointment_submit", {
        button_location: "appointment_form",
        service: form.service,
      });

      setForm({
        name: "",
        phone: "",
        email: "",
        service: "General Consultation",
        preferred_date: "",
        preferred_time: "",
        message: "",
      });

      setDateOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail[0]?.msg
        : (typeof detail === "string" ? detail : null);

      toast.error(
        msg || "Could not send just now. Please WhatsApp or call +91 86015 63494."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#f6f7f4]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-6">
          <div className="section-eyebrow">Visit or Call</div>
          <h2 className="mt-4 font-serif-display text-[42px] md:text-[60px] leading-[1.05] text-[#0e2b2b]">
            Book your <span className="hero-italic">appointment</span>
          </h2>
          <p className="mt-5 text-[15px] md:text-[16px] text-[#0e2b2b]/70 max-w-[500px]">
            Same-day consultations available. Share your details and we&rsquo;ll call you within 10 minutes to confirm a convenient slot.
          </p>

          <div className="mt-10 space-y-5">
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 rounded-full bg-white border border-[#0e2b2b]/10 flex items-center justify-center text-[#14a68e]"><MapPin size={16} /></span>
              <div>
                <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0e2b2b]/60">Address</div>
                <div className="mt-1 text-[15px] text-[#0e2b2b]">{clinicInfo.address.line1}<br />{clinicInfo.address.line2}<br />{clinicInfo.address.line3}</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 rounded-full bg-white border border-[#0e2b2b]/10 flex items-center justify-center text-[#14a68e]"><Phone size={16} /></span>
              <div>
                <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0e2b2b]/60">Phone</div>
                <a href={`tel:${clinicInfo.phone}`} className="mt-1 block text-[15px] text-[#0e2b2b] hover:text-[#14a68e]">{clinicInfo.phone}</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 rounded-full bg-white border border-[#0e2b2b]/10 flex items-center justify-center text-[#14a68e]"><Clock size={16} /></span>
              <div>
                <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0e2b2b]/60">Hours</div>
                <div className="mt-1 text-[15px] text-[#0e2b2b] leading-6">{clinicInfo.hours.weekday}<br />{clinicInfo.hours.sunday}<br /><span className="text-[#0e2b2b]/60">{clinicInfo.hours.saturday}</span></div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <a href={clinicInfo.whatsapp} target="_blank" rel="noreferrer" className="btn-ghost"><MessageCircle size={16} /> WhatsApp</a>
              <a href={clinicInfo.instagram} target="_blank" rel="noreferrer" className="btn-ghost"><Instagram size={16} /> Instagram</a>
              <a href={clinicInfo.mapsUrl} target="_blank" rel="noreferrer" className="btn-ghost"><MapPin size={16} /> Directions</a>
            </div>
          </div>

          {/* Map embed */}
          <div className="mt-8 rounded-[24px] overflow-hidden border border-[#0e2b2b]/10 shadow-sm">
            <iframe
              title="Dr. Khurana's Dental & Aesthetic Clinic location"
              src={clinicInfo.mapsEmbed}
              className="w-full h-[280px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>

        <div className="lg:col-span-6">
          <form onSubmit={submit} onFocus={wakeBackend} className="bg-white rounded-[28px] p-5 sm:p-6 md:p-8 border border-[#0e2b2b]/10 shadow-sm overflow-visible">
            <div className="font-serif-display text-[24px] text-[#0e2b2b]">Request a callback</div>
            <div className="text-[13px] text-[#0e2b2b]/60 mt-1">We&rsquo;ll respond within 10 minutes during clinic hours.</div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="appointment-name" className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0e2b2b]/60">Full Name</label>
                <input id="appointment-name" name="name" autoComplete="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 w-full h-11 px-4 rounded-full border border-[#0e2b2b]/15 bg-white focus:outline-none focus:border-[#14a68e] focus:ring-2 focus:ring-[#14a68e]/20 text-[14px]" placeholder="Your name" />
              </div>
              <div>
                <label htmlFor="appointment-phone" className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0e2b2b]/60">WhatsApp Number</label>
                <input id="appointment-phone" name="phone" type="tel" autoComplete="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 w-full h-11 px-4 rounded-full border border-[#0e2b2b]/15 bg-white focus:outline-none focus:border-[#14a68e] focus:ring-2 focus:ring-[#14a68e]/20 text-[14px]" placeholder="+91 86015 63494" />
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="appointment-email" className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0e2b2b]/60">Email (optional)</label>
              <input id="appointment-email" name="email" type="email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 w-full h-11 px-4 rounded-full border border-[#0e2b2b]/15 bg-white focus:outline-none focus:border-[#14a68e] focus:ring-2 focus:ring-[#14a68e]/20 text-[14px]" placeholder="you@email.com" />
            </div>

            <div className="mt-4">
              <label htmlFor="appointment-service" className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0e2b2b]/60">Service</label>
              <select id="appointment-service" name="service" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="mt-2 w-full h-11 px-4 rounded-full border border-[#0e2b2b]/15 bg-white focus:outline-none focus:border-[#14a68e] focus:ring-2 focus:ring-[#14a68e]/20 text-[14px]">
                {["General Consultation", "Root Canal", "Teeth Whitening", "Braces / Aligners", "Dental Implants", "Crowns & Bridges", "Pediatric Dentistry", "Aesthetic Dentistry"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>

              <div className="mt-5">
                <label className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0e2b2b]/60">
                  Preferred Date
                </label>

                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <button
                      id="appointment-date"
                      type="button"
                      onPointerEnter={loadCalendar}
                      onFocus={loadCalendar}
                      onTouchStart={loadCalendar}
                      className="mt-2 w-full h-12 px-4 rounded-2xl border border-[#0e2b2b]/15 bg-white flex items-center justify-between gap-3 text-left focus:outline-none focus:border-[#14a68e] focus:ring-2 focus:ring-[#14a68e]/20"
                    >
                      <span className={form.preferred_date ? "text-[14px] text-[#0e2b2b]" : "text-[14px] text-[#0e2b2b]/45"}>
                        {form.preferred_date
                          ? formatDisplayDate(new Date(`${form.preferred_date}T12:00:00`))
                          : "Choose a date"}
                      </span>
                      <CalendarDays size={18} className="text-[#14a68e] shrink-0" />
                    </button>
                  </PopoverTrigger>

                  <PopoverContent
                    align="start"
                    className="w-[calc(100vw-32px)] max-w-[320px] p-0 rounded-2xl border border-[#0e2b2b]/10 shadow-xl bg-white"
                  >
                    <Suspense fallback={null}>
                    <Calendar
                      mode="single"
                      className="mx-auto p-3"
                      selected={
                        form.preferred_date
                          ? new Date(`${form.preferred_date}T12:00:00`)
                          : undefined
                      }
                      onSelect={(date) => {
                        if (!date) return;

                        setForm({
                          ...form,
                          preferred_date: formatISODate(date),
                        });
                        setDateOpen(false);
                      }}
                      disabled={[
                        { before: startOfToday() },
                        { dayOfWeek: [6] },
                      ]}
                      classNames={{
                        day_disabled:
                          "text-[#0e2b2b]/25 opacity-40 cursor-not-allowed line-through",
                      }}
                    />
                    </Suspense>
                  </PopoverContent>
                </Popover>

                <div className="mt-2 text-[12px] text-[#0e2b2b]/50">
                  Saturdays are unavailable because the clinic is closed.
                </div>
              </div>

              <div className="mt-5">
                <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0e2b2b]/60">
                  Preferred Time
                </div>

                <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    {
                      value: "Morning (10 AM - 1 PM)",
                      title: "Morning",
                      time: "10 AM - 1 PM",
                      icon: Sun,
                    },
                    {
                      value: "Afternoon (1 PM - 5 PM)",
                      title: "Afternoon",
                      time: "1 PM - 5 PM",
                      icon: Sun,
                    },
                    {
                      value: "Evening (5 PM - 9 PM)",
                      title: "Evening",
                      time: "5 PM - 9 PM",
                      icon: Sunset,
                    },
                  ].map((slot) => {
                    const Icon = slot.icon;
                    const selected = form.preferred_time === slot.value;

                    return (
                      <button
                        key={slot.value}
                        type="button"
                        aria-pressed={selected}
                        onClick={() =>
                          setForm({ ...form, preferred_time: slot.value })
                        }
                        className={`min-h-[92px] rounded-2xl border px-3 py-3 text-center transition ${
                          selected
                            ? "border-[#14a68e] bg-[#eaf6f2] ring-2 ring-[#14a68e]/15"
                            : "border-[#0e2b2b]/15 bg-white hover:border-[#14a68e]/60"
                        }`}
                      >
                        <Icon size={18} className="mx-auto text-[#14a68e]" />
                        <div className="mt-2 text-[13px] font-semibold text-[#0e2b2b]">
                          {slot.title}
                        </div>
                        <div className="mt-1 text-[11px] text-[#0e2b2b]/55">
                          {slot.time}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            <div className="mt-4">
              <label htmlFor="appointment-message" className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0e2b2b]/60">Message (optional)</label>
              <textarea id="appointment-message" name="message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 w-full min-h-[100px] px-4 py-3 rounded-2xl border border-[#0e2b2b]/15 bg-white focus:outline-none focus:border-[#14a68e] focus:ring-2 focus:ring-[#14a68e]/20 text-[14px]" placeholder="Tell us briefly what you need" />
            </div>

            <button disabled={loading} type="submit" className="btn-primary mt-6 w-full justify-center">
              {loading ? "Sending…" : (<>Book Appointment <Send size={14} /></>)}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
